import React, { useCallback, useState } from 'react';
import {
  Box,
  Center,
  Icon,
  Text,
  VStack,
  useColorModeValue,
  Image,
  Progress,
} from '@chakra-ui/react';
import { useDropzone } from 'react-dropzone';
import { FiUpload, FiFile, FiImage } from 'react-icons/fi';

const FileUploader = ({ onFileUpload, isUploading, accept = '*', maxSize = 20 * 1024 * 1024 }) => {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  
  const onDrop = useCallback((acceptedFiles) => {
    // 最初のファイルのみ受け付け
    const selectedFile = acceptedFiles[0];
    if (selectedFile) {
      setFile(selectedFile);
      
      // 画像の場合はプレビューを表示
      if (selectedFile.type.startsWith('image/')) {
        const fileReader = new FileReader();
        fileReader.onload = () => {
          setPreview(fileReader.result);
        };
        fileReader.readAsDataURL(selectedFile);
      } else {
        // 画像以外の場合はプレビューをクリア
        setPreview(null);
      }
      
      // 即座にファイルアップロード処理を実行
      if (onFileUpload) {
        onFileUpload(selectedFile);
      }
    }
  }, [onFileUpload]);
  
  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: accept ? { 
      'application/pdf': ['.pdf'],
      'image/jpeg': ['.jpg', '.jpeg'],
      'image/png': ['.png'],
      'image/tiff': ['.tiff', '.tif'],
    } : undefined,
    maxSize: maxSize,
    multiple: false,
  });
  
  const handleUpload = () => {
    if (file) {
      onFileUpload(file);
    }
  };
  
  const getFileIcon = () => {
    if (!file) return <FiUpload size={50} />;
    
    if (file.type === 'application/pdf') {
      return <FiFile size={50} />;
    } else if (file.type.startsWith('image/')) {
      return <FiImage size={50} />;
    }
    
    return <FiFile size={50} />;
  };
  
  const getFileTypeText = () => {
    if (!file) return '';
    
    if (file.type === 'application/pdf') {
      return 'PDF';
    } else if (file.type.startsWith('image/')) {
      return file.type.split('/')[1].toUpperCase();
    }
    
    return file.type;
  };
  
  return (
    <VStack spacing={4} width="100%">
      <Box
        {...getRootProps()}
        border="2px dashed"
        borderColor={isDragActive ? 'brand.500' : 'gray.300'}
        borderRadius="md"
        py={10}
        px={4}
        width="100%"
        textAlign="center"
        bg={useColorModeValue(
          isDragActive ? 'brand.50' : 'white',
          isDragActive ? 'brand.900' : 'gray.800'
        )}
        transition="all 0.2s"
        _hover={{
          borderColor: 'brand.500',
          bg: useColorModeValue('brand.50', 'brand.900'),
        }}
        cursor="pointer"
      >
        <input {...getInputProps()} />
        
        <Center>
          <VStack spacing={3}>
            {preview ? (
              <Image
                src={preview}
                alt="プレビュー"
                maxH="150px"
                objectFit="contain"
                borderRadius="md"
              />
            ) : (
              <Icon as={() => getFileIcon()} color={isDragActive ? 'brand.500' : 'gray.400'} />
            )}
            
            <VStack spacing={1}>
              <Text fontWeight="bold">
                {isDragActive
                  ? 'ここにファイルをドロップ'
                  : file
                  ? `選択済み: ${file.name}`
                  : 'クリックまたはドラッグ＆ドロップでファイルを選択'}
              </Text>
              
              {file && (
                <Text fontSize="sm" color="gray.500">
                  {getFileTypeText()} - {(file.size / 1024 / 1024).toFixed(2)} MB
                </Text>
              )}
              
              {!file && (
                <Text fontSize="sm" color="gray.500">
                  PDF, JPG, PNG, TIFF ファイルをサポート (最大20MB)
                </Text>
              )}
            </VStack>
          </VStack>
        </Center>
      </Box>
      
      {isUploading && <Progress size="xs" isIndeterminate width="100%" colorScheme="brand" />}
    </VStack>
  );
};

export default FileUploader;